import { useEffect, useRef, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { HERO_VIDEOS } from '../assets/media';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(26px); }
  to { opacity: 1; transform: translateY(0); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.7; }
  50% { transform: translateY(10px); opacity: 1; }
`;

const Wrap = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 520px;
  max-height: 960px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgDarker};
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 900ms ease;
`;

/* Los videos son verticales (reels); se centran y recortan como fondo ambiental */
const Video = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
  filter: grayscale(0.25) brightness(0.75);
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(18, 16, 12, 0.5);
`;

/* Cuña diagonal color crema que "corta" el hero por abajo, como el resto del sitio */
const DiagonalCut = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: -1px;
  height: 9vw;
  max-height: 110px;
  min-height: 46px;
  background: ${({ theme }) => theme.colors.bg};
  clip-path: polygon(0 62%, 100% 0%, 100% 100%, 0% 100%);
  z-index: 2;
  pointer-events: none;
`;

const ScrollCue = styled.button`
  position: absolute;
  bottom: calc(9vw + 1.25rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  color: #f4f3ee;
  cursor: pointer;
  animation: ${bounce} 1.8s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* Texto fijo de marca sobre los videos rotando */
const Content = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 1.25rem;
`;

const Kicker = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  color: #f4f3ee;
  margin: 0 0 0.9rem;
  animation: ${fadeUp} 800ms ease both 150ms;
`;

const TitleBox = styled.div`
  border: 3px solid #f4f3ee;
  padding: 1.2rem 2rem 1.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  animation: ${fadeUp} 800ms ease both 350ms;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.8rem, 6vw, 4rem);
  font-weight: 800;
  letter-spacing: 0.16em;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.6);
`;

const Tagline = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(0.65rem, 1.6vw, 0.85rem);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  color: #d8d5cc;
  margin: 0;
`;

const SerifWord = styled.p`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.6rem, 4.5vw, 2.6rem);
  font-weight: 600;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: #ffffff;
  margin: -1.2rem 0 0;
  position: relative;
  z-index: 3;
  background: transparent;
  padding: 0 1rem;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.8);
  animation: ${fadeUp} 800ms ease both 550ms;
`;

const Slashes = styled.p`
  color: ${({ theme }) => theme.colors.gold};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: 700;
  letter-spacing: 0.4em;
  margin: 1.2rem 0 0;
  animation: ${fadeUp} 800ms ease both 750ms;
`;

const Controls = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  gap: 0.6rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  min-width: 12px;
  min-height: 12px;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ theme, $active }) => ($active ? theme.colors.gold : 'rgba(255,255,255,0.35)')};
  transition: background 200ms ease;
`;

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => ($side === 'left' ? 'left: 0.75rem;' : 'right: 0.75rem;')}
  z-index: 3;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  cursor: pointer;
  font-size: 1.4rem;

  &:hover {
    background: ${({ theme }) => theme.colors.gold};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const AUTO_ADVANCE_MS = 9000;

// NOTA: por ahora el hero usa los videos locales de public/assets en vez de
// GET /api/banners. Cuando se retome la conexión con el banner administrable,
// esta función vuelve a hacer fetch a /api/banners igual que antes.
function BannerCarousel() {
  const [index, setIndex] = useState(0);
  const videoRefs = useRef([]);

  const next = useCallback(() => {
    setIndex((i) => (i + 1) % HERO_VIDEOS.length);
  }, []);

  const prev = useCallback(() => {
    setIndex((i) => (i - 1 + HERO_VIDEOS.length) % HERO_VIDEOS.length);
  }, []);

  useEffect(() => {
    if (HERO_VIDEOS.length < 2) return undefined;
    const id = setInterval(next, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [next]);

  // Solo el video activo reproduce; el resto se pausa para no gastar recursos.
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === index) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [index]);

  return (
    <Wrap role="region" aria-label="Video del bar">
      {HERO_VIDEOS.map((src, i) => (
        <Slide key={src} $active={i === index} aria-hidden={i !== index}>
          <Video
            ref={(el) => {
              videoRefs.current[i] = el;
            }}
            src={src}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
          />
          <Scrim />
        </Slide>
      ))}

      <Content>
        <Kicker>Bienvenidos a</Kicker>
        <TitleBox>
          <Title>Vete a la Chingada</Title>
          <Tagline>Toma rico · Llora bonito · Supéralo</Tagline>
        </TitleBox>
        <SerifWord>Despecho Bar</SerifWord>
        <Slashes aria-hidden="true">/ — —</Slashes>
      </Content>

      <Arrow $side="left" type="button" aria-label="Video anterior" onClick={prev}>
        ‹
      </Arrow>
      <Arrow $side="right" type="button" aria-label="Video siguiente" onClick={next}>
        ›
      </Arrow>
      <Controls>
        {HERO_VIDEOS.map((src, i) => (
          <Dot
            key={src}
            type="button"
            $active={i === index}
            aria-label={`Ir al video ${i + 1}`}
            aria-current={i === index}
            onClick={() => setIndex(i)}
          />
        ))}
      </Controls>

      <ScrollCue
        type="button"
        aria-label="Bajar a la siguiente sección"
        onClick={() =>
          document.getElementById('menu-home')?.scrollIntoView({ behavior: 'smooth' })
        }
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 4V20M12 20L6 14M12 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </ScrollCue>

      <DiagonalCut aria-hidden="true" />
    </Wrap>
  );
}

export default BannerCarousel;
